import { CommonRequestOptions } from 'generated-service-lib';
import {
  Application,
  ApplicationPermissionSet,
  AuthorizationClient,
  AuthorizationUser,
  Client,
  ClientAttribute,
  ClientAttributeValue,
  Permission,
  Role,
  RolePermission,
  UserDTO,
  UserPermission,
} from 'generated-service-lib/AuthService';
import { GeneratedAuthService } from '../generatedServices';
import { getValidResponseDataOrEmptyArray, getValidResponseDataOrThrow } from '../helpers';
import { User } from '../types';

export class AuthService {
  readonly unvalidatedClient: GeneratedAuthService;
  constructor(domain?: string) {
    this.unvalidatedClient = new GeneratedAuthService(domain);
  }

  getClientById = async (clientId: string) => {
    return await this.unvalidatedClient
      .getAuthClientsByClientId({
        clientId,
      })
      .then(getValidResponseDataOrThrow);
  };

  getClientAttributes = async (clientId: string) => {
    return await this.unvalidatedClient
      .getAuthClientsByClientIdAttributes({ clientId })
      .then(getValidResponseDataOrThrow);
  };
  postClientAttribute = async (clientId: string, body: ClientAttribute): Promise<Client> =>
    this.unvalidatedClient.postAuthClientsByClientIdAttributes({ clientId, body }).then((res) => {
      if (typeof res.body === 'string') {
        throw new Error(res.body);
      }
      return res?.body?.data ?? {};
    });

  deleteClientAttribute = async (clientId: string, attributeId: string): Promise<Client> =>
    this.unvalidatedClient.deleteAuthClientsByClientIdAttributesByAttributeId({ clientId, attributeId }).then((res) => {
      if (typeof res.body === 'string') {
        throw res;
      }
      return res.body.data as Client;
    });

  getApplications = async (body: { showServices?: boolean } = {}): Promise<Application[]> => {
    return await this.unvalidatedClient.getAuthApplications(body).then(getValidResponseDataOrThrow);
  };

  postApplication = async (app: { name: string; clientId: string; redirectURLs: string[] }) => {
    const createdApp = await this.unvalidatedClient
      .postAuthApplications({
        body: {
          name: app.name,
          redirectURLs: app.redirectURLs,
        },
      })
      .then(getValidResponseDataOrThrow);

    return await this.unvalidatedClient
      .postAuthClientsByClientIdApplications({
        clientId: app.clientId,
        body: {
          id: createdApp.id,
          name: app.name,
        },
      })
      .then(getValidResponseDataOrThrow);
  };

  getApplicationById = async (appId: string): Promise<Application> => {
    return await this.unvalidatedClient.getAuthApplicationsById({ id: appId }).then(getValidResponseDataOrThrow);
  };

  putApplicationById = async (id: string, authApp: Application): Promise<Application> => {
    return await this.unvalidatedClient
      .putAuthApplicationsByApplicationId({ applicationId: id, body: authApp })
      .then(getValidResponseDataOrThrow);
  };

  getPermissionsForApp = async (appId: string) => {
    return await this.unvalidatedClient
      .getAuthApplicationsByIdPermissions({ id: appId })
      .then(getValidResponseDataOrThrow);
  };

  postPermissionForApp = async (appId: string, permission: Permission) => {
    if (permission.id) permission.id = permission.id.replace(/ /g, '').toLowerCase();
    return await this.unvalidatedClient
      .postAuthApplicationsByIdPermissions({ id: appId, body: permission })
      .then(getValidResponseDataOrThrow);
  };

  postAppPermissionsToUser = async (userId: string, appId: string, body: Permission): Promise<UserPermission> => {
    return await this.unvalidatedClient
      .postAuthPermissionsUsersByUserIdApplicationsByAppIdPermissions({
        userId,
        appId,
        body,
      })
      .then((res) => (typeof res.body === 'undefined' ? {} : (res.body as UserPermission)));
  };

  deleteAppPermissionFromUser = async (userId: string, appId: string, permission: string) => {
    return await this.unvalidatedClient
      .deleteAuthPermissionsUsersByUserIdApplicationsByAppIdPermissionsByPermission({ userId, appId, permission })
      .then((res) => (typeof res.body === 'undefined' ? {} : res.body));
  };

  postAppRolesToUser = async (userId: string, appId: string, body: Role): Promise<UserPermission> => {
    return await this.unvalidatedClient
      .postAuthPermissionsUsersByUserIdApplicationsByAppIdRoles({
        userId,
        appId,
        body,
      })
      .then((res) => (typeof res.body === 'undefined' ? {} : (res.body as UserPermission)));
  };

  postClientAttributeToUser = async (userId: string, clientId: string, body: ClientAttributeValue) =>
    this.unvalidatedClient
      .postAuthUsersByUserIdClientsByClientIdAttributes({ userId, clientId, body })
      .then(getValidResponseDataOrThrow);
  putClientAttributeToUser = async (
    userId: string,
    clientId: string,
    attributeId: string,
    body: ClientAttributeValue
  ) =>
    this.unvalidatedClient
      .putAuthUsersByUserIdClientsByClientIdAttributesByAttributeId({ userId, clientId, attributeId, body })
      .then(getValidResponseDataOrThrow);
  deleteClientAttributeFromUser = async (userId: string, clientId: string, attributeId: string) =>
    this.unvalidatedClient
      .deleteAuthUsersByUserIdClientsByClientIdAttributesByAttributeId({ userId, clientId, attributeId })
      .then(getValidResponseDataOrThrow);
  putPermissionForApp = async (appId: string, permission: Permission & { id: string }) => {
    return await this.unvalidatedClient
      .putAuthApplicationsByIdPermissionsByPermissionId({ id: appId, permissionId: permission.id, body: permission })
      .then(getValidResponseDataOrThrow);
  };

  deletePermissionForApp = async (appId: string, permission: string) => {
    return await this.unvalidatedClient
      .deleteAuthApplicationsByIdPermissionsByPermission({ id: appId, permission: permission })
      .then(getValidResponseDataOrThrow);
  };

  getRolesForApp = async (appId: string) => {
    return await this.unvalidatedClient.getAuthApplicationsByIdRoles({ id: appId }).then(getValidResponseDataOrThrow);
  };

  postRoleForApp = async (appId: string, role: Role) => {
    if (role.id) role.id = role.id.replace(/ /g, '').toLowerCase();
    return await this.unvalidatedClient
      .postAuthApplicationsByIdRoles({ id: appId, body: role })
      .then(getValidResponseDataOrThrow);
  };

  putRoleForApp = async (appId: string, role: Role & { id: string }) => {
    return await this.unvalidatedClient
      .putAuthApplicationsByIdRolesByRoleId({ id: appId, roleId: role.id, body: role })
      .then(getValidResponseDataOrThrow);
  };

  deleteRoleForApp = async (appId: string, roleId: string) => {
    return await this.unvalidatedClient
      .deleteAuthApplicationsByIdRolesByRole({ id: appId, role: roleId })
      .then(getValidResponseDataOrThrow);
  };

  postRolePermissionForApp = async (appId: string, rolePerm: RolePermission) => {
    if (rolePerm.role === undefined || rolePerm.role.id === undefined) return;
    rolePerm.role.id = rolePerm.role.id.replace(/ /g, '').toLowerCase();
    return await this.unvalidatedClient
      .postAuthApplicationsByIdRolesByRolePermissions({ id: appId, role: rolePerm.role.id, body: rolePerm })
      .then(getValidResponseDataOrThrow);
  };

  putRolePermissionForApp = async (appId: string, rolePerm: RolePermission) => {
    if (rolePerm.role === undefined || rolePerm.role.id === undefined) return;
    return await this.unvalidatedClient
      .putAuthApplicationsByIdRolesByRolePermissions({ id: appId, role: rolePerm.role.id, body: rolePerm })
      .then(getValidResponseDataOrThrow);
  };

  getUsers = async () => {
    return await this.unvalidatedClient
      .getAuthGraphUsers({})
      .then((res) => (typeof res.body === 'undefined' ? {} : res.body))
      .then((body) => (typeof body.data === 'undefined' ? ([] as User[]) : (body.data as User[])));
  };

  getUserById = async (userId: string) => {
    return await this.unvalidatedClient.getAuthUsersByUserId({ userId }).then(getValidResponseDataOrThrow);
  };

  getAuthUsers = async (options: CommonRequestOptions = {}) => {
    return await this.unvalidatedClient
      .getAuthUsers(options)
      .then((res) => (typeof res.body === 'undefined' ? {} : res.body))
      .then((body) =>
        typeof body.data === 'undefined' ? ([] as AuthorizationUser[]) : (body.data as AuthorizationUser[])
      );
  };

  getAuthPermissionsUsersByUserId = async (body: { userId: string }): Promise<ApplicationPermissionSet[]> => {
    return await this.unvalidatedClient
      .getAuthPermissionsUsersByUserId(body)
      .then((res) => (typeof res.body === 'undefined' ? {} : res.body))
      .then((body) => (typeof body.data === 'undefined' ? [] : (body.data as ApplicationPermissionSet[])));
  };
  getUserPermissionsByAppId = async (appId: string) => {
    return await this.unvalidatedClient
      .getAuthApplicationsByIdUsers({ id: appId })
      .then(getValidResponseDataOrEmptyArray);
  };

  getUsersByClientId = async (clientId: string) => {
    return await this.unvalidatedClient
      .getAuthClientsByClientIdUsers({ clientId })
      .then(getValidResponseDataOrEmptyArray);
  };

  // create new user
  postUser = async (newUser: UserDTO): Promise<AuthorizationUser> => {
    return await this.unvalidatedClient
      .postAuthUsers({
        body: newUser,
      })
      .then((res) => (typeof res.body === 'undefined' ? {} : res.body))
      .then((body) =>
        typeof body.data === 'undefined' ? ({} as AuthorizationUser) : (body.data as AuthorizationUser)
      );
  };

  updateUser = async (userId: string, body: AuthorizationUser): Promise<AuthorizationUser> => {
    return await this.unvalidatedClient
      .postAuthUsersByUserId({ userId, body })
      .then((res) => (typeof res.body === 'undefined' ? {} : res.body))
      .then((body) =>
        typeof body.data === 'undefined' ? ({} as AuthorizationUser) : (body.data as AuthorizationUser)
      );
  };

  addClientToUser = async (userId: string, body: AuthorizationClient): Promise<AuthorizationUser> => {
    return await this.unvalidatedClient
      .postAuthUsersByUserIdClients({ userId, body })
      .then((res) => (typeof res.body === 'undefined' ? {} : res.body))
      .then((body) =>
        typeof body.data === 'undefined' ? ({} as AuthorizationUser) : (body.data as AuthorizationUser)
      );
  };

  removeClientFromUser = async (userId: string, clientId: string): Promise<AuthorizationUser> => {
    return await this.unvalidatedClient
      .deleteAuthUsersByUserIdClientsByClientId({ userId, clientId })
      .then((res) => (typeof res.body === 'undefined' ? {} : res.body))
      .then((body) =>
        typeof body.data === 'undefined' ? ({} as AuthorizationUser) : (body.data as AuthorizationUser)
      );
  };

  deleteUser = async (userId: string): Promise<any> => {
    return await this.unvalidatedClient.deleteAuthUsersByUserId({ userId });
  };

  postRoleToUser = async (appId: string, userId: string, role: Role) => {
    return await this.unvalidatedClient
      .postAuthPermissionsUsersByUserIdApplicationsByAppIdRoles({ userId: userId, appId: appId, body: role })
      .then(getValidResponseDataOrThrow);
  };

  deleteRoleFromUser = async (appId: string, userId: string, roleId: string) => {
    return await this.unvalidatedClient.deleteAuthPermissionsUsersByUserIdApplicationsByAppIdRolesByRole({
      userId: userId,
      appId: appId,
      role: roleId,
    }); // currently returns void - cannot use .then(getValidResponseDataOrThrow);
  };

  getClients = async () => {
    return await this.unvalidatedClient.getAuthClients({}).then(getValidResponseDataOrEmptyArray);
  };

  postClient = async (client: { id: string; name: string }) => {
    return await this.unvalidatedClient
      .postAuthClients({
        body: {
          id: client.id,
          clientName: client.name,
        },
      })
      .then(getValidResponseDataOrThrow);
  };

  deleteClient = async (clientId: string) => {
    return await this.unvalidatedClient.deleteAuthClientsByClientId({
      clientId,
    });
  };
}
